import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { View } from '../../types';
import VetDashboard from './VetDashboard';
import { LoginView, RegisterView } from '../../components/AuthViews';
import { 
    Stethoscope, ShieldAlert, ArrowLeft, Heart,
    Zap, CheckCircle2, ArrowRight, Package, MapPin,
    Activity, ShieldCheck, Mail, LogOut,
    Users, Calendar, ChevronRight
} from 'lucide-react';

interface VetPortalViewProps {
    onNavigate: (view: View) => void;
}

export const VetPortalView: React.FC<VetPortalViewProps> = ({ onNavigate }) => {
    const { user, logout } = useAuth();
    const [activePortalTab, setActivePortalTab] = useState<'discover' | 'login' | 'register'>('login');

    // Sync browser tab or query parameters if they are set (for seamless direct link support)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const action = params.get('action');
        if (action === 'login') {
            setActivePortalTab('login');
        } else if (action === 'register' || params.get('role') === 'vet') {
            setActivePortalTab('register');
        }
    }, []);

    // Custom inner navigation interceptor to keep vet auth experience inside /veterinaria
    const handleInnerNavigate = (view: View) => {
        console.log("VetPortalView Intercepted navigation:", view);
        if (view === View.REGISTER) {
            setActivePortalTab('register');
        } else if (view === View.LOGIN) {
            setActivePortalTab('login');
        } else if (view === View.LANDING || view === View.VET_LANDING) {
            setActivePortalTab('discover');
        } else if (view === View.VET_DASHBOARD) {
            // Already logged in, the auth state change will automatically trigger rendering the Dashboard
            console.log("Logged in successfully, waiting for auth context update");
        } else {
            onNavigate(view);
        }
    };

    const handleLogout = () => {
        if (confirm("¿Estás seguro de que quieres cerrar sesión del Portal Pro?")) {
            logout();
            setActivePortalTab('login');
        }
    };

    // CASE 1: Veterinarian is Logged In
    if (user && user.role?.includes('vet')) {
        return (
            <div className="min-h-screen bg-slate-50 w-full animate-in fade-in duration-500">
                <VetDashboard onLogout={handleLogout} />
            </div>
        );
    }

    // CASE 2: Non-Veterinarian User is Logged In (Restricted Access Screen)
    if (user && user.role !== 'vet') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6 relative overflow-hidden font-sans">
                {/* Glowing aesthetic backgrounds */}
                <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none"></div>
                <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-rose-500/10 rounded-full blur-[100px] pointer-events-none"></div>

                <div className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl p-10 md:p-12 rounded-[3rem] shadow-2xl max-w-xl w-full text-center relative z-10 animate-in zoom-in-95 duration-500">
                    <div className="w-20 h-20 bg-rose-500/10 border border-rose-500/30 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl shadow-rose-950/20">
                        <ShieldAlert size={40} className="text-rose-500 animate-pulse" />
                    </div>

                    <h2 className="text-3xl font-black text-white uppercase italic tracking-tighter mb-4">
                        Acceso Restringido Pro
                    </h2>
                    
                    <p className="text-slate-400 font-medium text-sm leading-relaxed mb-8 max-w-md mx-auto">
                        Esta sección del ecosistema <span className="text-indigo-400 font-bold">Amigo Peludo Pro</span> está reservada exclusivamente para médicos veterinarios y administradores de clínicas veterinarias.
                    </p>

                    <div className="bg-slate-950/50 rounded-2xl p-6 mb-8 border border-slate-800 text-left">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Usuario Activo</p>
                        <p className="text-sm font-black text-indigo-300 italic uppercase">{user.name}</p>
                        <p className="text-xs text-slate-500 font-medium mt-0.5">{user.email}</p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <button
                            onClick={() => {
                                logout();
                                setActivePortalTab('login');
                            }}
                            className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-4 px-6 rounded-2xl font-black uppercase text-xs tracking-widest transition-all active:scale-95 border border-slate-700"
                        >
                            Cambiar de Cuenta
                        </button>
                        <button
                            onClick={() => onNavigate(View.USER_HUB)}
                            className="flex-1 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white py-4 px-6 rounded-2xl font-black uppercase text-xs tracking-widest transition-all shadow-lg shadow-indigo-900/30 active:scale-95 flex items-center justify-center gap-2"
                        >
                            Volver al Panel General <ArrowRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // CASE 3: Guest / Not Logged In (Landing + Login + Register Hub)
    return (
        <div className="bg-slate-50 min-h-screen overflow-x-hidden font-sans selection:bg-indigo-600 selection:text-white pb-20">
            {/* Top Branded Navigation Header */}
            <nav className="fixed top-0 left-0 w-full bg-white/80 backdrop-blur-md z-50 border-b border-slate-100 px-6 py-4 pt-[calc(1rem+env(safe-area-inset-top))]">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <button 
                        onClick={() => {
                            setActivePortalTab('discover');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                        className="flex items-center gap-2.5 group"
                    >
                        <div className="bg-indigo-600 p-2.5 rounded-2xl text-white group-hover:rotate-12 transition-transform shadow-lg shadow-indigo-100">
                            <Stethoscope size={20} />
                        </div>
                        <div className="text-left">
                            <span className="font-black text-slate-800 italic uppercase tracking-tighter text-base leading-none block">Amigo Peludo Pro</span>
                            <span className="text-[8px] font-black text-indigo-600 tracking-[0.25em] uppercase block mt-0.5">VET ECOSYSTEM</span>
                        </div>
                    </button>

                    {/* Navigation Actions — visible en todos los tamaños */}
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <button
                            onClick={() => setActivePortalTab('discover')}
                            className={`hidden sm:flex px-3 md:px-4 py-2 md:py-2.5 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'discover'
                                    ? 'bg-indigo-50 text-indigo-600'
                                    : 'text-slate-500 hover:text-slate-800'
                            }`}
                        >
                            Descubrir
                        </button>
                        <button
                            onClick={() => setActivePortalTab('login')}
                            className={`px-3 sm:px-4 md:px-5 py-2 md:py-2.5 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'login'
                                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                                    : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            Ingresar
                        </button>
                        <button
                            onClick={() => setActivePortalTab('register')}
                            className={`px-3 sm:px-4 md:px-5 py-2 md:py-2.5 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'register'
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-slate-900 text-white hover:bg-indigo-600'
                            }`}
                        >
                            <span className="hidden sm:inline">Solicitar </span>Cuenta
                        </button>
                    </div>
                </div>
            </nav>

            {/* Content Switcher */}
            <div className="pt-20 md:pt-24 max-w-7xl mx-auto px-6 animate-in fade-in duration-300">
                {activePortalTab === 'discover' && (
                    <div className="animate-in fade-in slide-in-from-bottom-6 duration-500">
                        {/* Hero Section */}
                        <section className="relative py-12 md:py-20 overflow-hidden text-center lg:text-left">
                            <div className="flex flex-col lg:flex-row items-center gap-12 md:gap-16">
                                <div className="flex-1">
                                    <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-600 px-4.5 py-2.5 rounded-full font-black text-[10px] uppercase tracking-widest mb-6">
                                        <Zap size={14} className="animate-pulse" /> Ecosistema para Veterinarias
                                    </div>
                                    <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-slate-900 leading-[1.1] mb-6 uppercase italic tracking-tighter">
                                        Digitaliza tu Clínica y <span className="text-indigo-600">Conecta</span> con Dueños
                                    </h1>
                                    <p className="text-slate-500 text-base md:text-lg font-semibold leading-relaxed mb-8 max-w-2xl mx-auto lg:mx-0">
                                        Amigo Peludo Pro es la herramienta definitiva para médicos veterinarios. Centraliza consultas, automatiza carnets de salud y fideliza a tus clientes a través de nuestra App masiva.
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                        <button
                                            onClick={() => setActivePortalTab('register')}
                                            className="bg-indigo-600 text-white px-10 py-4.5 rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-xl shadow-indigo-200 hover:bg-slate-900 transition-all flex items-center justify-center gap-2.5"
                                        >
                                            Empezar Ahora <ArrowRight size={16} />
                                        </button>
                                        <button
                                            onClick={() => setActivePortalTab('login')}
                                            className="bg-white border-2 border-slate-200 text-slate-600 px-10 py-4.5 rounded-2xl font-black uppercase text-xs tracking-[0.2em] hover:bg-slate-50 transition-all"
                                        >
                                            Ya tengo cuenta
                                        </button>
                                    </div>
                                </div>
                                <div className="flex-1 relative w-full max-w-md lg:max-w-none mx-auto">
                                    <div className="relative z-10 rounded-[3rem] overflow-hidden shadow-2xl border-8 border-white bg-slate-100">
                                        <img
                                            src="https://images.unsplash.com/photo-1576091160550-217359f42f8c?auto=format&fit=crop&q=80&w=800"
                                            alt="Portal Preview"
                                            className="w-full h-auto"
                                            onError={(e: any) => { e.target.src = 'https://images.unsplash.com/photo-1628009368231-7bb7cfcb0def?auto=format&fit=crop&q=80&w=800'; }}
                                        />
                                    </div>
                                    <div className="absolute -top-10 -right-10 w-64 h-64 bg-indigo-400/10 rounded-full blur-3xl -z-10"></div>
                                    <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-violet-400/10 rounded-full blur-3xl -z-10"></div>
                                </div>
                            </div>
                        </section>

                        {/* Features Showcase */}
                        <section className="bg-white rounded-[3.5rem] py-16 md:py-24 px-6 md:px-12 my-12 border border-slate-200/50 shadow-xl">
                            <div className="text-center mb-16">
                                <h2 className="text-3xl md:text-4xl font-black text-slate-900 uppercase italic tracking-tighter mb-4">Herramientas Diseñadas para Médicos</h2>
                                <p className="text-slate-400 font-bold uppercase text-xs tracking-widest">Tecnología que potencia tu práctica profesional</p>
                                <p className="text-slate-500 text-base font-medium leading-relaxed max-w-3xl mx-auto mt-4">
                                    Software de gestión veterinaria integral: agenda de citas online, historial clínico digital, control de inventario de medicamentos y farmacología veterinaria. Automatiza recordatorios de vacunas y desparasitaciones para fidelizar clientes. El sistema de administración que toda clínica veterinaria moderna necesita.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                                <div className="flex flex-col gap-6 group">
                                    <div className="rounded-[2rem] overflow-hidden shadow-lg border-4 border-slate-50 relative">
                                        <img
                                            src="https://images.unsplash.com/photo-1584031767622-5a3bd1333742?auto=format&fit=crop&q=80&w=800"
                                            alt="Inventory"
                                            className="w-full h-auto group-hover:scale-105 transition-transform duration-700"
                                            onError={(e: any) => { e.target.src = 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&q=80&w=800'; }}
                                        />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter mb-3.5 flex items-center gap-3">
                                            <Package className="text-indigo-600" /> Control de Stock e Inventario
                                        </h3>
                                        <p className="text-slate-500 font-medium text-sm leading-relaxed">
                                            Gestiona tu farmacia y tienda de forma inteligente. Recibe alertas en tiempo real cuando un medicamento esté por agotarse o vencer, asegurando que nunca detengas tu atención.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-6 group">
                                    <div className="rounded-[2rem] overflow-hidden shadow-lg border-4 border-slate-50 relative">
                                        <img
                                            src="https://images.unsplash.com/photo-1581579186913-45ac3e6efe93?auto=format&fit=crop&q=80&w=800"
                                            alt="Map Position"
                                            className="w-full h-auto group-hover:scale-105 transition-transform duration-700"
                                            onError={(e: any) => { e.target.src = 'https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=800'; }}
                                        />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter mb-3.5 flex items-center gap-3">
                                            <MapPin className="text-indigo-600" /> Posicionamiento en el Mapa
                                        </h3>
                                        <p className="text-slate-500 font-medium text-sm leading-relaxed">
                                            Aparece como un nodo destacado en el mapa de Amigo Peludo. Los usuarios locales podrán ver tu disponibilidad, calificación y agendar citas directamente desde la App.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Plans Grid */}
                        <section className="py-12 px-2">
                            <div className="text-center mb-16">
                                <h2 className="text-3xl md:text-4xl font-black text-slate-900 uppercase italic tracking-tighter mb-4">Elige tu Nivel de Alianza</h2>
                                <p className="text-slate-400 font-bold uppercase text-xs tracking-widest">Planes adaptados al tamaño de tu clínica</p>
                                <p className="text-slate-500 text-sm font-medium leading-relaxed max-w-2xl mx-auto mt-3">
                                    Desde médicos veterinarios independientes hasta hospitales veterinarios con múltiples especialistas. Todos los planes incluyen carnet sanitario digital automático, citas online y visibilidad destacada en el mapa de servicios veterinarios de tu ciudad.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                                {/* Plan Essentials */}
                                <div className="bg-white p-10 md:p-12 rounded-[3rem] border-4 border-slate-50 shadow-xl flex flex-col group hover:border-indigo-500 transition-all duration-300">
                                    <div className="mb-8">
                                        <h3 className="text-2xl font-black text-slate-900 uppercase italic mb-2">Plan Essential</h3>
                                        <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest">Especial para Consultas Externas</p>
                                    </div>
                                    <ul className="space-y-4 mb-10 flex-1">
                                        {[
                                            'Registro de Consultas Médicas',
                                            'Carnet Sanitario Digital Automático',
                                            'Historial Clínico en la Nube',
                                            'Notificaciones de Cobro (Demo)',
                                            'Visualización básica en la lista'
                                        ].map((item, i) => (
                                            <li key={i} className="flex items-start gap-3 text-sm font-bold text-slate-600">
                                                <CheckCircle2 size={18} className="text-indigo-600 shrink-0 mt-0.5" /> <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => setActivePortalTab('register')}
                                        className="w-full py-4.5 rounded-2xl border-2 border-indigo-600 text-indigo-600 font-black uppercase text-xs tracking-widest hover:bg-indigo-600 hover:text-white transition-all shadow-md active:scale-95"
                                    >
                                        Seleccionar Essentials
                                    </button>
                                </div>

                                {/* Plan Pro */}
                                <div className="bg-slate-900 p-10 md:p-12 rounded-[3rem] shadow-2xl flex flex-col relative overflow-hidden group border border-slate-800">
                                    <div className="absolute top-0 right-0 bg-indigo-500 text-white px-5 py-2.5 rounded-bl-2xl font-black text-[9px] uppercase tracking-widest">MÁS POPULAR</div>
                                    <div className="mb-8">
                                        <h3 className="text-2xl font-black text-white uppercase italic mb-2">Plan Pro-Clinic</h3>
                                        <p className="text-indigo-300 text-[10px] font-black uppercase tracking-widest">Gestión Integral de Negocio</p>
                                    </div>
                                    <ul className="space-y-4 mb-10 flex-1">
                                        {[
                                            'Todo lo de Plan Essentials',
                                            'Portal de Inventario y Alarmas de Stock',
                                            'Posicionamiento Destacado en el Mapa',
                                            'Envío de Alertas Masivas a Usuarios locales',
                                            'Perfil de Clínica Premium con Fotos y Logo',
                                            'Soporte Prioritario'
                                        ].map((item, i) => (
                                            <li key={i} className="flex items-start gap-3 text-sm font-bold text-slate-300">
                                                <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" /> <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => setActivePortalTab('register')}
                                        className="w-full bg-indigo-600 text-white py-4.5 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-indigo-500 transition-all shadow-xl shadow-indigo-600/20 active:scale-95"
                                    >
                                        Seleccionar Pro-Clinic
                                    </button>
                                </div>
                            </div>
                        </section>
                    </div>
                )}

                {activePortalTab === 'login' && (
                    <div className="max-w-md mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {/* Injected Login View locked to VET with inner navigate interceptor */}
                        <LoginView 
                            initialMode="VET" 
                            hideRegister={true} 
                            onNavigate={handleInnerNavigate} 
                        />
                    </div>
                )}

                {activePortalTab === 'register' && (
                    <div className="max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {/* Injected Register View locked to Vet with inner navigate interceptor */}
                        <RegisterView 
                            role="vet" 
                            onNavigate={handleInnerNavigate} 
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
export default VetPortalView;
